import { navigationStickActions, settingMenu } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.redux';
import { selectNavigationStick } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.selectors';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClassicDocumentIcon from 'app/assets/images/IconImages/ClassicDocumentIcon.svg';
import WebDocumentIcon from 'app/assets/images/IconImages/WebDocumentIcon.svg';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { ITagType, ITagTypeAddRequest, TagTypesFilter } from 'app/models/Candidate/Tags';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import {
  addTagType,
  getTagTypesByCategory,
  updateTagType,
  updateTagTypeStatus,
} from 'app/services/CandidateServices/CandidateServices';
import { httpSuccess } from 'app/services/serviceHelpers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDocumentTitle from 'utils/customHooks/useDocumentTitle';
import { selectUser } from 'oidc/user.selectors';
import { CandidateTagsSearchTab } from './CandidateTagsSearchTab';

/** Form uses isClassicTag (matches list/UI); add API expects isDirectTag where isClassicTag = !isDirectTag */
type TagTypeAddFormData = Omit<ITagTypeAddRequest, 'isDirectTag'> & { isClassicTag: boolean };

const defaultFormData: TagTypeAddFormData = {
  description: '',
  fullDescription: '',
  tagCategory: 'Traveler',
  isClassicTag: false,
  isActive: true,
  isReadOnly: false,
  lastUpdatedById: null,
  note: '',
};

const CATEGORY_OPTIONS = [
  { label: 'Traveler', value: 'Traveler' },
  { label: 'Traveler and Company', value: 'Traveler and Company' },
];

const renderWebClassicTagIcon = (isClassicTag: boolean, disabled = false) =>
  isClassicTag ? (
    <img src={ClassicDocumentIcon} alt="" style={disabled ? { opacity: 0.5 } : undefined} />
  ) : (
    <img src={WebDocumentIcon} alt="" style={disabled ? { opacity: 0.5 } : undefined} />
  );

const mapRowToFormData = (row: ITagType): TagTypeAddFormData => ({
  description: row.description,
  fullDescription: row.fullDescription ?? '',
  tagCategory: row.category,
  isClassicTag: row.isClassicTag,
  isActive: row.isActive,
  lastUpdatedById: null,
  note: row.note ?? '',
});

export const TagsManagementPage = () => {
  useDocumentTitle({ title: 'Tags Management' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedMenu } = useSelector(selectNavigationStick);
  const user = useSelector(selectUser);

  const [sectionTab, setSectionTab] = useState<'manage' | 'candidateTags'>('manage');
  const [filter, setFilter] = useState<TagTypesFilter>('all');
  const [rows, setRows] = useState<ITagType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tagTypeDialogMode, setTagTypeDialogMode] = useState<'add' | 'edit' | null>(null);
  const [editingRow, setEditingRow] = useState<ITagType | null>(null);
  const [formData, setFormData] = useState<TagTypeAddFormData>(defaultFormData);
  const [readOnlyConfirmOpen, setReadOnlyConfirmOpen] = useState(false);
  const isAddMode = tagTypeDialogMode === 'add';
  const isEditMode = tagTypeDialogMode === 'edit';
  const isTagTypeDialogOpen = tagTypeDialogMode !== null;
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<{ description: string }>({
    mode: 'onChange',
    defaultValues: { description: defaultFormData.description },
  });

  const loadRows = useCallback(
    async (activeFilter: TagTypesFilter) => {
      setIsLoading(true);
      setError('');
      const response = await getTagTypesByCategory(activeFilter, { withTimestamp: true });
      if (httpSuccess(response?.status)) {
        setRows(Array.isArray(response?.data) ? response.data : []);
      } else {
        setError(t('tagsManagement.messages.loadFail'));
      }
      setIsLoading(false);
    },
    [t],
  );

  useEffect(() => {
    loadRows(filter);
  }, [filter, loadRows]);

  useEffect(() => {
    setError('');
    setSuccess('');
  }, [sectionTab]);

  const headerTitleKey =
    sectionTab === 'manage' ? 'tagsManagement.header.manage.title' : 'tagsManagement.header.candidateTags.title';
  const headerSubtitleKey =
    sectionTab === 'manage'
      ? 'tagsManagement.header.manage.subtitle'
      : 'tagsManagement.header.candidateTags.subtitle';

  useEffect(() => {
    if (!['settings', 'tagsManagement'].includes(selectedMenu?.key)) {
      const subMenu = settingMenu.subMenu.find(x => x.key === 'tagsManagement');
      dispatch(navigationStickActions.setSelectedMenuByKey('settings'));
      dispatch(navigationStickActions.setSelectedSubMenu(subMenu));
    }
  }, [selectedMenu?.key, dispatch]);

  const isAddDisabled = useMemo(() => !formData.description?.trim() || !formData.tagCategory?.trim() || !isValid, [formData, isValid]);

  const closeTagTypeDialog = () => {
    setTagTypeDialogMode(null);
    setEditingRow(null);
    setFormData(defaultFormData);
    setReadOnlyConfirmOpen(false);
    reset({ description: defaultFormData.description });
  };

  const openAddDialog = () => {
    setEditingRow(null);
    setFormData(defaultFormData);
    reset({ description: defaultFormData.description });
    setTagTypeDialogMode('add');
  };

  const openEditDialog = (row: ITagType) => {
    if (row.isHidden) return;
    const nextFormData = mapRowToFormData(row);
    setEditingRow(row);
    setFormData(nextFormData);
    reset({ description: nextFormData.description });
    setTagTypeDialogMode('edit');
  };

  useEffect(() => {
    if (isAddMode) {
      reset({ description: formData.description || '' });
    }
  }, [isAddMode, reset, formData.description]);

  const handleAddTagType = async ({ description }: { description: string }) => {
    if (isAddDisabled) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    const response = await addTagType({
      description: description.trim(),
      tagCategory: formData.tagCategory.trim(),
      isDirectTag: !formData.isClassicTag,
      isActive: formData.isActive ?? true,
      isReadOnly: formData.isReadOnly ?? false,
      lastUpdatedById: user?.userInfo?.employeeId || null,
      fullDescription: formData.fullDescription?.trim() || null,
      note: formData.note?.trim() || null,
    });
    const isSuccess = httpSuccess(response?.status) && response?.data?.responseCode === 0;
    if (isSuccess) {
      setSuccess(t('tagsManagement.messages.addSuccess'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.addSuccess'),
          severity: 'success',
        }),
      );
      closeTagTypeDialog();
      await loadRows(filter);
    } else {
      setError(t('tagsManagement.messages.addFail'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.addFail'),
          severity: 'error',
        }),
      );
    }
    setIsSaving(false);
  };

  const handleUpdateTagType = async () => {
    if (!editingRow?.description?.trim()) return;
    setIsSaving(true);
    setError('');
    setSuccess('');
    const response = await updateTagType({
      description: editingRow.description.trim(),
      fullDescription: formData.fullDescription?.trim() || null,
      note: formData.note?.trim() || null,
      isActive: formData.isActive ?? true,
      lastUpdatedById: user?.userInfo?.employeeId || 0,
    });
    const isSuccess = httpSuccess(response?.status) && response?.data?.responseCode === 0;
    if (isSuccess) {
      setSuccess(t('tagsManagement.messages.updateSuccess'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.updateSuccess'),
          severity: 'success',
        }),
      );
      closeTagTypeDialog();
      await loadRows(filter);
    } else {
      setError(t('tagsManagement.messages.updateFail'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.updateFail'),
          severity: 'error',
        }),
      );
    }
    setIsSaving(false);
  };

  const handleStatusChange = async (row: ITagType, checked: boolean) => {
    setError('');
    setSuccess('');
    const response = await updateTagTypeStatus({
      description: row.description,
      status: checked,
      lastUpdatedById: user?.userInfo?.employeeId || 0,
    });
    const isSuccess = httpSuccess(response?.status) && response?.data?.responseCode === 0;
    if (isSuccess) {
      setRows(prev => prev.map(item => (item.id === row.id ? { ...item, isActive: checked } : item)));
      setSuccess(t('tagsManagement.messages.statusSuccess'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.statusSuccess'),
          severity: 'success',
        }),
      );
    } else {
      setError(t('tagsManagement.messages.statusFail'));
      dispatch(
        globalActions.setSnackBar({
          message: response?.data?.message || t('tagsManagement.messages.statusFail'),
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f9fafb', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff', px: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Tabs
          value={sectionTab}
          onChange={(_, value: 'manage' | 'candidateTags') => setSectionTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            flex: 1,
            minWidth: 0,
            '& .MuiTab-root': { minHeight: 44, py: 1.25, fontSize: '0.8rem', textTransform: 'none' },
            '& .MuiTabs-indicator': { bgcolor: '#3f2d8f' },
          }}
        >
          <Tab label={t('tagsManagement.tabs.manage')} value="manage" />
          <Tab label={t('tagsManagement.tabs.candidateTags')} value="candidateTags" />
        </Tabs>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>{t(headerTitleKey)}</Typography>
          <Typography variant="body2" color="text.secondary">{t(headerSubtitleKey)}</Typography>
        </Box>

        {sectionTab === 'manage' && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sectionTab === 'manage' && success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {sectionTab === 'manage' && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Select
                size="small"
                value={filter}
                onChange={e => setFilter(e.target.value as TagTypesFilter)}
              sx={{
                minWidth: 120,
                '& .MuiSelect-select': { py: 0.65, fontSize: '0.8rem' },
                '& .MuiOutlinedInput-root': { height: 34, borderRadius: 1.5 },
              }}
              >
                <MenuItem value="all">{t('tagsManagement.filters.all')}</MenuItem>
                <MenuItem value="active">{t('tagsManagement.filters.active')}</MenuItem>
              </Select>
            <Button
              variant="outlined"
              size="small"
              onClick={() => loadRows(filter)}
              disabled={isLoading}
              sx={{ height: 34, textTransform: 'none', fontSize: '0.8rem', borderRadius: 1.5, px: 2 }}
            >
                {t('tagsManagement.actions.refresh')}
              </Button>
            <Button
              variant="contained"
              size="small"
              onClick={openAddDialog}
              sx={{ height: 34, textTransform: 'none', fontSize: '0.8rem', borderRadius: 1.5, px: 2, bgcolor: '#3f2d8f' }}
            >
                {t('tagsManagement.actions.add')}
              </Button>
            </Stack>

            <Box sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">{t('tagsManagement.columns.webClassic')}</TableCell>
                    <TableCell>{t('tagsManagement.columns.description')}</TableCell>
                    <TableCell>{t('tagsManagement.columns.fullDescription')}</TableCell>
                    <TableCell>{t('tagsManagement.columns.category')}</TableCell>
                    <TableCell>{t('tagsManagement.columns.active')}</TableCell>
                    <TableCell align="center">{t('tagsManagement.columns.edit')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={16} />
                          <Typography>{t('tagsManagement.messages.loading')}</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map(row => (
                    <TableRow
                      key={row.id}
                      sx={
                        row.isHidden
                          ? {
                              opacity: 0.55,
                              backgroundColor: 'action.hover',
                              '& .MuiTableCell-root': {
                                color: 'text.disabled',
                              },
                            }
                          : undefined
                      }
                    >
                      <TableCell align="center">
                        <Tooltip
                          title={
                            row.isClassicTag
                              ? t('tagsManagement.labels.classicTag')
                              : t('tagsManagement.labels.web')
                          }
                          placement="top"
                        >
                          <Stack direction="row" alignItems="center" justifyContent="center">
                            {renderWebClassicTagIcon(row.isClassicTag, row.isHidden)}
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.fullDescription || '--'}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          disabled={row.isHidden}
                          control={
                            <Switch
                              checked={!!row.isActive}
                              disabled={row.isHidden}
                              onChange={e => handleStatusChange(row, e.target.checked)}
                            />
                          }
                          label={row.isActive ? t('tagsManagement.labels.active') : t('tagsManagement.labels.inactive')}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {!row.isHidden && (
                          <IconButton
                            size="small"
                            aria-label={t('tagsManagement.dialogs.editTitle')}
                            onClick={() => openEditDialog(row)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography>{t('tagsManagement.messages.noRecords')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}

        {sectionTab === 'candidateTags' && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CandidateTagsSearchTab />
          </Box>
        )}
      </Box>

      <GenericDialog
        open={isTagTypeDialogOpen}
        variant="blue"
        fullWidth
        maxWidth="md"
        onClose={closeTagTypeDialog}
        PaperProps={{
          sx: {
            maxHeight: { xs: '92vh', sm: '88vh' },
          },
        }}
        dialogTitleProps={{
          text: isEditMode ? t('tagsManagement.dialogs.editTitle') : t('tagsManagement.dialogs.addTitle'),
          closeButton: true,
        }}
        dialogContentProps={{
          sx: {
            overflowY: 'auto',
            maxHeight: { xs: 'calc(92vh - 160px)', sm: 'calc(88vh - 170px)' },
          },
        }}
        dialogActions={[
          {
            text: t('tagsManagement.actions.cancel'),
            variant: 'contained',
            color: 'tertiary',
            onClick: closeTagTypeDialog,
          },
          {
            text: t('tagsManagement.actions.save'),
            variant: 'contained',
            disabled: isSaving || (isAddMode && isAddDisabled),
            onClick: isEditMode ? handleUpdateTagType : handleSubmit(handleAddTagType),
          },
        ]}
      >
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {t('tagsManagement.fields.description')}
            </Typography>
            {isEditMode ? (
              <TextField
                variant="outlined"
                size="small"
                value={formData.description}
                fullWidth
                disabled
              />
            ) : (
              <Controller
                control={control}
                name="description"
                rules={{
                  required: t('required'),
                  validate: value => {
                    const normalized = value?.trim().toUpperCase();
                    if (!normalized) return t('required');
                    const exists = rows.some(
                      row => row?.description?.trim().toUpperCase() === normalized,
                    );
                    return exists ? t('tagsManagement.messages.duplicateDescription') : true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    placeholder={t('tagsManagement.placeholders.description')}
                    value={field.value ?? ''}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({ ...prev, description: e.target.value }));
                    }}
                    fullWidth
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || ''}
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {t('tagsManagement.fields.category')}
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              select
              placeholder={t('tagsManagement.placeholders.category')}
              value={formData.tagCategory}
              onChange={e => setFormData(prev => ({ ...prev, tagCategory: e.target.value }))}
              fullWidth
              required
              disabled={isEditMode}
            >
              {CATEGORY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('tagsManagement.fields.fullDescription')}
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder={t('tagsManagement.placeholders.fullDescription')}
              value={formData.fullDescription ?? ''}
              onChange={e => setFormData(prev => ({ ...prev, fullDescription: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('tagsManagement.fields.note')}
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder={t('tagsManagement.placeholders.note')}
              value={formData.note ?? ''}
              onChange={e => setFormData(prev => ({ ...prev, note: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={4}>
              <FormControlLabel
                disabled
                control={
                  <Switch
                    checked={!!formData.isClassicTag}
                    disabled
                    onChange={e => setFormData(prev => ({ ...prev, isClassicTag: e.target.checked }))}
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">{t('tagsManagement.fields.directTag')}</Typography>
                    <Tooltip title={t('tagsManagement.tooltips.directTag')}>
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.isActive}
                    onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">{t('tagsManagement.fields.active')}</Typography>
                    <Tooltip title={t('tagsManagement.tooltips.active')}>
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                }
              />
              {isAddMode && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.isReadOnly}
                      onChange={e => {
                        if (e.target.checked) {
                          setReadOnlyConfirmOpen(true);
                        } else {
                          setFormData(prev => ({ ...prev, isReadOnly: false }));
                        }
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{t('tagsManagement.fields.readOnly')}</Typography>
                      <Tooltip title={t('tagsManagement.tooltips.readOnly')}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Stack>
                  }
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </GenericDialog>

      <GenericDialog
        open={readOnlyConfirmOpen}
        variant="white"
        onClose={() => setReadOnlyConfirmOpen(false)}
        dialogTitleProps={{
          text: t('tagsManagement.dialogs.readOnlyConfirmTitle'),
          closeButton: true,
        }}
        dialogActions={[
          {
            text: t('tagsManagement.actions.cancel'),
            variant: 'contained',
            color: 'tertiary',
            onClick: () => setReadOnlyConfirmOpen(false),
          },
          {
            text: t('Ok'),
            variant: 'contained',
            onClick: () => {
              setFormData(prev => ({ ...prev, isReadOnly: true }));
              setReadOnlyConfirmOpen(false);
            },
          },
        ]}
      >
        <Typography>{t('tagsManagement.dialogs.readOnlyConfirmMessage')}</Typography>
      </GenericDialog>
    </Box>
  );
};
